import React from 'react';

import { Box, Paper, Typography } from '@mui/material';

import {
  DataDisputeByIdDocument,
  DataDisputeItemInfoFragment,
  DataDisputeItemStatus,
  InboxItemsScrollableListDocument,
  useDataDisputeItemChangeStatusMutation,
} from 'src/api';
import { FullscreenLoader } from 'src/components/common';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import {
  useCurrentInboxItemDataDispute,
  useInboxItemParamId,
  useInboxItemUnreadStatuses,
} from 'src/hooks/inboxItem';
import { useModalV2 } from 'src/hooks/shared';
import { EntityActionConfirmationModal } from 'src/modals/common';

import { DataDisputeItemInfo, NavigateToEmployeeButton, VerificationRequestLink } from './shared';
import { paperStyles } from './styles';
import { DataDisputeVRConnectionStatus } from './types';
import { checkDataDisputeVerificationRequestStatus } from './utils';

export const InboxItemContentDataDisputeCVStaff: React.FC = () => {
  const { openModal } = useModalV2();
  const { createApolloContext } = useNotification();

  const { dataDispute, loading } = useCurrentInboxItemDataDispute();

  const inboxItemId = useInboxItemParamId();
  const { updateInboxItemUnreadStatuses } = useInboxItemUnreadStatuses(inboxItemId);

  const dataDisputeVerificationRequestStatus =
    checkDataDisputeVerificationRequestStatus(dataDispute);

  const [dataDisputeItemChangeStatusMutation] = useDataDisputeItemChangeStatusMutation({
    context: createApolloContext({ successMessage: 'Data Dispute has been successfully resolved' }),
    refetchQueries: [InboxItemsScrollableListDocument, DataDisputeByIdDocument],
  });

  const handleResolve = React.useCallback(
    (dataDisputeItem: DataDisputeItemInfoFragment) => {
      openModal(EntityActionConfirmationModal, {
        id: ModalId.EntityActionConfirmation,
        action: 'resolve',
        entity: 'this Data Dispute item',
        onSubmit: async () => {
          if (!dataDisputeItem.id) {
            return;
          }
          await Promise.all([
            dataDisputeItemChangeStatusMutation({
              variables: {
                dataDisputeItemId: dataDisputeItem.id,
                status: DataDisputeItemStatus.Resolved,
              },
            }),
            updateInboxItemUnreadStatuses(),
          ]);
        },
      });
    },
    [dataDisputeItemChangeStatusMutation, openModal, updateInboxItemUnreadStatuses],
  );

  const subtitleText = React.useMemo((): React.ReactElement => {
    switch (dataDisputeVerificationRequestStatus) {
      case DataDisputeVRConnectionStatus.resolvedWithConnect:
        return (
          <React.Fragment>
            Employers have resolved Data Disputes based on&nbsp;
            <VerificationRequestLink verificationRequest={dataDispute?.verificationRequest} />.
            Please note that the changes may take effect in up to 3 days.
          </React.Fragment>
        );

      case DataDisputeVRConnectionStatus.resolvedNoConnect:
        return (
          <React.Fragment>
            Employers have resolved Data Disputes. Please note that the changes may take effect in
            up to 3 days.
          </React.Fragment>
        );

      case DataDisputeVRConnectionStatus.pendingWithConnect:
        return (
          <React.Fragment>
            Data Dispute based on&nbsp;
            <VerificationRequestLink verificationRequest={dataDispute?.verificationRequest} />
            &nbsp; was sent to Employer
          </React.Fragment>
        );

      default:
        return <React.Fragment>Data Dispute was sent to Employer</React.Fragment>;
    }
  }, [dataDispute?.verificationRequest, dataDisputeVerificationRequestStatus]);

  return (
    <Paper elevation={0} sx={paperStyles}>
      {loading ? (
        <FullscreenLoader />
      ) : (
        <React.Fragment>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
          >
            <Typography variant="h6">Employer Resolution Status</Typography>
            <NavigateToEmployeeButton employeeId={dataDispute?.employee.id} />
          </Box>

          <Typography variant="body1" sx={theme => ({ color: theme.palette.grey['700'] })} mt={1}>
            {subtitleText}
          </Typography>

          <Box mt={3} display="flex" flexDirection="column" gap={3}>
            {dataDispute?.dataDisputeItemsList.map(dataDisputeInfo => (
              <DataDisputeItemInfo
                key={dataDisputeInfo.id}
                dataDisputeItem={dataDisputeInfo}
                onResolve={handleResolve}
              />
            ))}
          </Box>
        </React.Fragment>
      )}
    </Paper>
  );
};
